import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Loader2, ExternalLink } from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { gov } from '../../lib/govApi';
import { portalPath } from '../../lib/portal';
import { toast } from '../../hooks/use-toast';
import { statusBadge, statusLabel, GOV_STATUS_FILTERS } from '../../lib/statusBadge';

const GovOrders = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const params = filter === 'all' ? {} : { status_filter: filter };
      const r = await gov.orders(params);
      setOrders(r.data || []);
    } catch (e) {
      toast({ title: 'Error', description: e.response?.data?.detail || 'Failed to load orders', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  return (
    <div className="space-y-4" data-testid="gov-orders-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-800">Orders</h1>
          <p className="text-sm text-slate-500">All orders visible to you.</p>
        </div>
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="w-52" data-testid="gov-orders-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            {GOV_STATUS_FILTERS.map((s) => (
              <SelectItem key={s} value={s} data-testid={`gov-orders-status-${s}`}>{statusLabel(s)}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <Card className="border-2">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12"><Loader2 className="h-8 w-8 animate-spin text-blue-700" /></div>
          ) : orders.length === 0 ? (
            <p className="text-center text-slate-500 py-10">No orders to show.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="text-left p-3">Order</th>
                    <th className="text-left p-3">PO #</th>
                    <th className="text-left p-3">Agency</th>
                    <th className="text-left p-3">Purchaser</th>
                    <th className="text-left p-3">Items</th>
                    <th className="text-left p-3">Total</th>
                    <th className="text-left p-3">Status</th>
                    <th className="text-left p-3">Date</th>
                    <th className="text-left p-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {orders.map(o => {
                    const oid = o.order_id || o.id;
                    return (
                    <tr key={oid} className="border-t hover:bg-slate-50" data-testid={`gov-order-row-${oid}`}>
                      <td className="p-3 font-medium">{oid}</td>
                      <td className="p-3">{o.po_number || '—'}</td>
                      <td className="p-3 text-slate-600">{o.agency_name}</td>
                      <td className="p-3 text-slate-600">{o.purchaser_name}</td>
                      <td className="p-3">{(o.items || []).length}</td>
                      <td className="p-3 font-semibold text-blue-700">${Number(o.total || 0).toFixed(2)}</td>
                      <td className="p-3"><span className={`px-2 py-0.5 rounded text-xs border ${statusBadge(o.status)}`} data-testid={`gov-order-status-${oid}`}>{statusLabel(o.status)}</span></td>
                      <td className="p-3 text-slate-500">{new Date(o.created_at).toLocaleDateString()}</td>
                      <td className="p-3"><Link to={portalPath(`/orders/${oid}`)} className="text-blue-700 underline inline-flex items-center gap-1" data-testid={`gov-order-view-${oid}`}>View <ExternalLink className="h-3 w-3" /></Link></td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default GovOrders;
